import { IChartScope, PolicyType } from "@/api";
import { ExpandableFieldSet } from "@/components/shared/ExpandableFieldSet";
import { FieldComparisonDataGrid } from "@/pages/frontend/compare-in-charts/components/chart/FieldComparisonDataGrid";
import { Box, styled } from "@mui/material";
import React from "react";

const FieldsContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    overflow: "visible",
}));

interface FieldsListProps {
    scopes: IChartScope[];
    expandedPolicyTypes?: Set<PolicyType>;
    onPolicyTypeToggle?: (policyType: PolicyType) => void;
    searchValue?: string;
}

export const FieldsList: React.FC<FieldsListProps> = ({
    scopes,
    expandedPolicyTypes,
    onPolicyTypeToggle,
    searchValue = "",
}) => {
    const policyTypeMap = React.useMemo(() => {
        const map = new Map<PolicyType, boolean>();

        scopes.forEach((scope) => {
            scope.extracted_fields_v2?.forEach((fieldGroup) => {
                if (fieldGroup.scalar_fields.length > 0) {
                    const policyType = fieldGroup.policy_type as PolicyType;
                    if (!map.has(policyType)) {
                        map.set(policyType, true);
                    }
                }
            });
        });

        return map;
    }, [scopes]);

    const filteredPolicyTypes = React.useMemo(() => {
        const allPolicyTypes = Array.from(policyTypeMap.keys());

        if (!searchValue) {
            return allPolicyTypes;
        }

        return allPolicyTypes.filter((policyType) => {
            if (policyType.toLowerCase().includes(searchValue.toLowerCase())) {
                return true;
            }

            return scopes.some((scope) => {
                const fieldGroup = scope.extracted_fields_v2?.find(
                    (fg) => fg.policy_type === policyType
                );

                if (!fieldGroup) return false;

                return fieldGroup.scalar_fields.some((field) => {
                    if (
                        field.field_name
                            .toLowerCase()
                            .includes(searchValue.toLowerCase())
                    ) {
                        return true;
                    }

                    return field.items.some((item) =>
                        item.properties.some((prop) => {
                            if (
                                prop.property_name
                                    .toLowerCase()
                                    .includes(searchValue.toLowerCase())
                            ) {
                                return true;
                            }
                            if (
                                prop.value &&
                                prop.value
                                    .toLowerCase()
                                    .includes(searchValue.toLowerCase())
                            ) {
                                return true;
                            }
                            return false;
                        })
                    );
                });
            });
        });
    }, [policyTypeMap, scopes, searchValue]);

    return (
        <FieldsContainer>
            {filteredPolicyTypes.map((policyType) => (
                <ExpandableFieldSet
                    key={policyType}
                    title={policyType + " Fields"}
                    isExpanded={expandedPolicyTypes?.has(policyType)}
                    onToggle={() => onPolicyTypeToggle?.(policyType)}
                >
                    <FieldComparisonDataGrid
                        scopes={scopes}
                        policyType={policyType}
                    />
                </ExpandableFieldSet>
            ))}
        </FieldsContainer>
    );
};
